/**
 * Supply claims — the nursery's queue.
 *
 * When an orchard maps a block planted before Factree existed, it names the
 * nursery that grew the trees. That is a claim: the tree count comes from a
 * polygon somebody drew and the year is whatever they remembered. This screen
 * is where the nursery that actually grew them answers.
 *
 * What it is NOT: a licence workflow. Answering here grants nothing and
 * creates no agreement. The record behind each claim is marked pre-existing
 * and can never become active — a database constraint sees to that. All this
 * does is put a real number beside a remembered one, so a rights holder has
 * something better than a guess to look at.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ClipboardCheck, Loader2, ShieldAlert, Check, X, Pencil,
  Building2, MapPin, TreeDeciduous,
} from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { Card, CardContent } from '../../components/ui/Card';
import { PbrMark, PbrNotice } from '../../components/cultivar';
import cultivarService, { type SupplyClaim } from '../../services/cultivar';
import { useModuleLicense } from '../../hooks/useModuleLicense';

type Filter = 'unanswered' | 'confirmed' | 'corrected' | 'disputed' | 'all';

const FILTERS: { key: Filter; label: string }[] = [
  { key: 'unanswered', label: 'Needs an answer' },
  { key: 'confirmed', label: 'Confirmed' },
  { key: 'corrected', label: 'Corrected' },
  { key: 'disputed', label: 'Disputed' },
  { key: 'all', label: 'All' },
];

const STATUS_STYLES: Record<string, string> = {
  confirmed: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  corrected: 'bg-amber-50 text-amber-900 border-amber-200',
  disputed: 'bg-red-50 text-red-800 border-red-200',
};

const STATUS_LABELS: Record<string, string> = {
  confirmed: 'Confirmed',
  corrected: 'Corrected',
  disputed: 'Disputed',
};

function claimKey(c: SupplyClaim) {
  return c.cultivar_area_id;
}

function cultivarLabel(c: SupplyClaim) {
  if (!c.cultivar_name) return 'No cultivar recorded';
  return c.cultivar_trade_name ? `${c.cultivar_name} (${c.cultivar_trade_name})` : c.cultivar_name;
}

/** "Hillside Orchard › Home Block" — where the trees actually are. */
function placeLabel(c: SupplyClaim) {
  return [c.farm_name, c.block_name].filter(Boolean).join(' › ') || '—';
}

export default function SupplyClaimQueue() {
  const { hasAccess: canRead, loading: accessLoading } = useModuleLicense('cultivar', ['viewer', 'pbr', 'admin']);
  const { hasAccess: canAnswer } = useModuleLicense('cultivar', ['pbr', 'admin']);

  const [claims, setClaims] = useState<SupplyClaim[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('unanswered');

  // Per-claim editor state. Keyed by area id so two open corrections cannot
  // write into each other.
  const [correcting, setCorrecting] = useState<number | null>(null);
  const [counts, setCounts] = useState<Record<number, string>>({});
  const [years, setYears] = useState<Record<number, string>>({});
  const [notes, setNotes] = useState<Record<number, string>>({});
  const [saving, setSaving] = useState<number | null>(null);

  // Everything is fetched once and filtered in memory: a nursery's claims are
  // bounded by how many blocks name it, the tab counts need the whole set
  // anyway, and switching tabs should not cost a round trip.
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await cultivarService.listSupplyClaims();
      if (!res.success) {
        setError(res.error?.message || 'Could not load supply claims');
        return;
      }
      setClaims(res.data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load supply claims');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const countsByFilter = useMemo(() => {
    const out: Record<Filter, number> = {
      unanswered: 0, confirmed: 0, corrected: 0, disputed: 0, all: claims.length,
    };
    for (const c of claims) {
      if (!c.verification_status) out.unanswered += 1;
      else out[c.verification_status] += 1;
    }
    return out;
  }, [claims]);

  const visible = useMemo(() => {
    if (filter === 'all') return claims;
    if (filter === 'unanswered') return claims.filter(c => !c.verification_status);
    return claims.filter(c => c.verification_status === filter);
  }, [claims, filter]);

  // The question is always "what did we send this grower" — so the grower is
  // the heading, not the cultivar.
  const byOrchard = useMemo(() => {
    const groups = new Map<number, { name: string; items: SupplyClaim[] }>();
    for (const c of visible) {
      const g = groups.get(c.orchard_org_id) || { name: c.orchard_name, items: [] };
      g.items.push(c);
      groups.set(c.orchard_org_id, g);
    }
    return Array.from(groups.entries());
  }, [visible]);

  const answer = async (
    claim: SupplyClaim,
    status: 'confirmed' | 'corrected' | 'disputed'
  ) => {
    const id = claimKey(claim);
    const countRaw = counts[id];
    const yearRaw = years[id];

    if (status === 'corrected' && !countRaw && !yearRaw) {
      setError('A correction has to correct something — give the tree count, the year, or both.');
      return;
    }

    setSaving(id);
    setError(null);
    setInfo(null);
    try {
      const res = await cultivarService.verifySupplyClaim(id, {
        status,
        verified_tree_count: status === 'corrected' && countRaw ? Number(countRaw) : null,
        verified_planting_year: status === 'corrected' && yearRaw ? Number(yearRaw) : null,
        notes: notes[id] || undefined,
      });
      if (!res.success) throw new Error(res.error?.message || 'Could not save your answer');
      setInfo(
        status === 'disputed'
          ? `Recorded: ${claim.area_name} was not supplied by you.`
          : `Recorded your answer for ${claim.area_name}.`
      );
      setCorrecting(null);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save your answer');
    } finally {
      setSaving(null);
    }
  };

  const startCorrecting = (claim: SupplyClaim) => {
    const id = claimKey(claim);
    // Seed with whatever is on record so a one-field correction does not mean
    // retyping the field that was already right.
    setCounts(prev => ({
      ...prev,
      [id]: prev[id] ?? String(claim.verified_tree_count ?? claim.claimed_tree_count ?? ''),
    }));
    setYears(prev => ({
      ...prev,
      [id]: prev[id] ?? String(claim.verified_planting_year ?? claim.claimed_planting_year ?? ''),
    }));
    setCorrecting(id);
  };

  if (accessLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
      </div>
    );
  }

  if (!canRead) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <Card><CardContent className="p-6 flex items-start gap-3">
          <ShieldAlert className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="font-semibold">Cultivar seat required</h3>
            <p className="text-sm text-neutral-600">
              A cultivar licence seat is needed to see the blocks naming your nursery as supplier.
            </p>
          </div>
        </CardContent></Card>
      </div>
    );
  }

  return (
    <>
      <PageHeader
        title="Supply claims"
        subtitle="Blocks that name your nursery as the supplier of their trees"
        icon={<ClipboardCheck className="h-6 w-6 text-white" />}
      />
      <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-4">
        <Card>
          <CardContent className="p-4 text-sm text-neutral-600 space-y-1">
            <p>
              These are existing blocks a grower has mapped and attributed to you. The tree counts
              come from boundaries they drew and the years from what they recall — your records are
              better than both.
            </p>
            <p className="text-neutral-500">
              Answering records what you supplied. It grants nothing, signs nothing, and creates no
              licence; whatever arrangement covers these trees was made elsewhere and is unaffected.
            </p>
          </CardContent>
        </Card>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 rounded px-4 py-2 text-sm">{error}</div>
        )}
        {info && (
          <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 rounded px-4 py-2 text-sm">{info}</div>
        )}

        <PbrNotice />

        <div className="flex flex-wrap gap-2">
          {FILTERS.map(f => {
            const active = filter === f.key;
            return (
              <button
                key={f.key}
                type="button"
                aria-pressed={active}
                onClick={() => setFilter(f.key)}
                className={`px-3 py-1.5 rounded-full border text-sm ${
                active
                  ? 'bg-primary-600 border-primary-600 text-white'
                  : 'bg-white border-neutral-300 text-neutral-700 hover:bg-neutral-50'
              }`}
              >
                {f.label}
                <span className={`ml-1.5 text-xs ${active ? 'text-white/80' : 'text-neutral-500'}`}>
                  {countsByFilter[f.key]}
                </span>
              </button>
            );
          })}
        </div>

        {visible.length === 0 ? (
          <Card><CardContent className="p-8 text-center text-neutral-500 text-sm">
            {filter === 'unanswered'
              ? claims.length === 0
                ? 'No block names your nursery as its supplier yet.'
                : 'Nothing waiting on you — every claim has an answer.'
              : 'Nothing here.'}
          </CardContent></Card>
        ) : (
          byOrchard.map(([orgId, group]) => (
            <div key={orgId} className="space-y-2">
              <h2 className="flex items-center gap-2 text-sm font-semibold text-neutral-700 pt-2">
                <Building2 className="h-4 w-4 text-neutral-400 flex-shrink-0" />
                <span className="truncate">{group.name}</span>
                <span className="text-xs font-normal text-neutral-500 flex-shrink-0">
                  {group.items.length} {group.items.length === 1 ? 'block' : 'blocks'}
                </span>
              </h2>

              {group.items.map(claim => {
                const id = claimKey(claim);
                const busy = saving === id;
                const answered = claim.verification_status;
                const isCorrecting = correcting === id;

                return (
                  <Card key={id}>
                    <CardContent className="p-4 space-y-3">
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="font-semibold text-neutral-900 break-words">{claim.area_name}</p>
                          <p className="text-xs text-neutral-500 flex items-center gap-1">
                            <MapPin className="h-3 w-3 flex-shrink-0" />
                            <span className="break-words">{placeLabel(claim)}</span>
                          </p>
                        </div>
                        {answered && (
                          <span className={`text-xs px-2 py-0.5 rounded-full border flex-shrink-0 ${STATUS_STYLES[answered]}`}>
                            {STATUS_LABELS[answered]}
                          </span>
                        )}
                      </div>

                      <p className="text-sm text-neutral-700 flex items-center gap-1.5 flex-wrap">
                        <TreeDeciduous className="h-4 w-4 text-neutral-400 flex-shrink-0" />
                        <span className="break-words">{cultivarLabel(claim)}</span>
                        <PbrMark status={claim.cultivar_protection_status} />
                      </p>

                      {/* The grower's figures, labelled as theirs. */}
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm bg-neutral-50 border border-neutral-200 rounded-lg p-3">
                        <div>
                          <p className="text-xs text-neutral-500">Grower says trees</p>
                          <p className="font-medium tabular-nums">{claim.claimed_tree_count ?? '—'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-neutral-500">Grower says planted</p>
                          <p className="font-medium tabular-nums">{claim.claimed_planting_year ?? '—'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-neutral-500">Area</p>
                          <p className="font-medium tabular-nums">
                            {claim.area_hectares != null ? `${claim.area_hectares} ha` : '—'}
                          </p>
                        </div>
                      </div>

                      {/* An existing correction, so the disagreement stays visible
                          rather than being replaced by the answer. */}
                      {answered === 'corrected' && !isCorrecting && (
                        <div className="text-sm bg-amber-50 border border-amber-200 rounded-lg p-3">
                          <p className="text-amber-900">
                            You recorded{' '}
                            {claim.verified_tree_count != null && (
                              <strong className="tabular-nums">{claim.verified_tree_count} trees</strong>
                            )}
                            {claim.verified_tree_count != null && claim.verified_planting_year != null && ', '}
                            {claim.verified_planting_year != null && (
                              <>planted <strong className="tabular-nums">{claim.verified_planting_year}</strong></>
                            )}
                            .
                          </p>
                          {claim.verification_notes && (
                            <p className="text-xs text-amber-800 mt-1">{claim.verification_notes}</p>
                          )}
                        </div>
                      )}

                      {answered && answered !== 'corrected' && claim.verification_notes && !isCorrecting && (
                        <p className="text-xs text-neutral-500">{claim.verification_notes}</p>
                      )}

                      {!canAnswer ? (
                        <p className="text-xs text-neutral-500">
                          A PBR or admin cultivar seat is required to answer a claim.
                        </p>
                      ) : isCorrecting ? (
                        <div className="space-y-3 border-t border-neutral-100 pt-3">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div className="space-y-1">
                              <label className="block text-xs font-semibold text-neutral-700">
                                Trees you supplied
                              </label>
                              <input
                                type="number"
                                className="input-professional w-full text-sm"
                                value={counts[id] ?? ''}
                                onChange={e => setCounts({ ...counts, [id]: e.target.value })}
                              />
                            </div>
                            <div className="space-y-1">
                              <label className="block text-xs font-semibold text-neutral-700">
                                Year you supplied them
                              </label>
                              <input
                                type="number"
                                className="input-professional w-full text-sm"
                                value={years[id] ?? ''}
                                onChange={e => setYears({ ...years, [id]: e.target.value })}
                              />
                            </div>
                          </div>
                          <input
                            className="input-professional w-full text-sm"
                            placeholder="Notes (optional)"
                            value={notes[id] || ''}
                            onChange={e => setNotes({ ...notes, [id]: e.target.value })}
                          />
                          <div className="flex flex-wrap gap-2">
                            <Button size="sm" onClick={() => answer(claim, 'corrected')} disabled={busy}>
                              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Save correction'}
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => setCorrecting(null)} disabled={busy}>
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex flex-wrap gap-2 border-t border-neutral-100 pt-3">
                          <Button
                            size="sm"
                            onClick={() => answer(claim, 'confirmed')}
                            disabled={busy}
                          >
                            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                            <span className="ml-1.5">{answered ? 'Confirm instead' : 'Confirm'}</span>
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => startCorrecting(claim)} disabled={busy}>
                            <Pencil className="w-3.5 h-3.5" />
                            <span className="ml-1.5">{answered === 'corrected' ? 'Edit correction' : 'Correct'}</span>
                          </Button>
                          <Button variant="danger" size="sm" onClick={() => answer(claim, 'disputed')} disabled={busy}>
                            <X className="w-3.5 h-3.5" />
                            <span className="ml-1.5">Not ours</span>
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ))
        )}
      </div>
    </>
  );
}
